import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubNote } from "../../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../../components/site/NewcomerNote";

const title = "Swedish school in Spain – how schooling abroad works";
const description =
  "How the Swedish curriculum, supplementary Swedish tuition and distance learning work for Swedish children in Spain – and what applies when returning to Sweden.";

export const Route = createFileRoute("/en/swedish-school-in-spain")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/swedish-school-in-spain") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/swedish-school-in-spain"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="Swedish school in Spain"
        intro="Attending a Swedish school abroad follows the same curriculum as at home, but everyday life looks a little different. Here's how it works in practice, from preschool to sixth form."
      />

      <NewcomerNote />

      <Prose>
        <h2>Same curriculum, different setting</h2>
        <p>
          A Swedish school abroad teaches according to the Swedish curriculum, with Swedish
          syllabuses and timetables. The subjects are the same, the knowledge requirements are the
          same, and the teachers hold Swedish teaching qualifications. The difference lies in the
          context: pupils encounter Spanish and English every day, classes are smaller, and some
          teaching may be mixed-age since the pupil base is smaller than at a large school in
          Sweden.
        </p>
        <p>
          For families, that means children can move abroad, attend school in Spain and later
          return to a Swedish class without having to catch up on an entirely different system.
          Pupils receive written assessments each year up to and including Year 6.
        </p>

        <h2>Preschool and reception year</h2>
        <p>
          Preschool follows the Swedish curriculum, built on play and learning. Spanish and English
          come in naturally through everyday life – in songs, games and routines – rather than as
          separate lessons. The transition into the reception year happens within the same setting,
          which makes the step small for children.
        </p>

        <h2>The early years of primary school</h2>
        <p>
          From the reception year to Year 6, teaching takes place on site with qualified Swedish
          teachers. This is where the foundations in Swedish and maths are laid, while Spanish and
          English teaching is more extensive than at a school in Sweden. Small groups make it
          possible to tailor teaching individually, and digital tools such as laptops and iPads are
          part of learning.
        </p>

        <h2>Secondary school and sixth form</h2>
        <p>
          From Year 7 onwards, studies take place through established Swedish distance education
          providers – Sofia Distans for lower secondary and Hermods for upper secondary – with
          supervision and a study environment at the school. Pupils have their school day on site
          together with others, but course content, assessment and grades come from the distance
          school. The model works well for pupils who are self-directed and benefit from structure
          and support around them in everyday life.
        </p>
        <p>
          This model is also what makes it possible to combine studies with elite sport, for
          example in Malmö FF's football academy or the padel and tennis pathway, since study time
          can be arranged around training sessions.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="en" />
      </div>

      <Prose>

        <h2>Supplementary Swedish tuition</h2>
        <p>
          For children attending a Spanish or international school, we offer supplementary Swedish
          tuition – teaching in the Swedish language and about Sweden, as a complement to regular
          schooling. Our school is approved by the Swedish National Agency for Education for
          supplementary Swedish tuition. It's an option for families who, for various reasons, have
          chosen a local school but want their children to keep their Swedish.
        </p>

        <h2>Oversight and regulations</h2>
        <p>
          A Swedish school in Spain operates within two systems at once: it follows the Swedish
          curriculum while also being subject to oversight from the Spanish education authorities –
          in our case, Junta de Andalucía (the Spanish education authority for Andalusia, the
          equivalent of Skolverket for schools operating in Spain). That means requirements on
          premises, safety and operations under Spanish rules, alongside the Swedish educational
          content. Read more on our pages about{" "}
          <Link className="text-primary link-quiet" to="/en/swedish-national-agency-for-education">
            the Swedish National Agency for Education and supplementary Swedish tuition
          </Link>{" "}
          and{" "}
          <Link className="text-primary link-quiet" to="/en/junta-de-andalucia">
            Junta de Andalucía
          </Link>
          .
        </p>

        <h2>Returning to Sweden</h2>
        <p>
          The most common worry among parents is the return. Because teaching follows the Swedish
          curriculum, the pupil moves back into the same syllabuses their classmates at home have
          studied. Assessments and grades are issued within the Swedish system, which makes placing
          the pupil in the right year group straightforward. Many pupils also come home with
          stronger language skills and experience of changing environments – something that's
          difficult to practise any other way.
        </p>

        <h2>Good questions to ask</h2>
        <ul>
          <li>Which year groups are taught on site, and which are taught remotely?</li>
          <li>What does supervision look like for distance-learning pupils?</li>
          <li>How much Spanish and English do pupils study?</li>
          <li>How does student health work, and how is extra support provided?</li>
          <li>What does schooling cost, and what's included?</li>
        </ul>
        <p>
          For our answers, see{" "}
          <Link className="text-primary link-quiet" to="/en/grade-levels">
            grade levels
          </Link>{" "}
          and{" "}
          <Link className="text-primary link-quiet" to="/en/fees">
            fees
          </Link>
          . If you have more questions, you're welcome to contact the headteacher.
        </p>
      </Prose>

      <KnowledgeHubNote resource="myndigheter-och-papper" locale="en" />

      <GuideLinks current="/svensk-skola-i-spanien" locale="en" />
      <CtaBand
        title="Ready to take the next step?"
        text="Applications are accepted on a rolling basis, and it's fine to start mid-term."
      />
      <div className="h-20" />
    </>
  );
}
